/**
 * MobileSelect
 *
 * Drop-in replacement for <select> that avoids the OS native picker dialog on
 * Capacitor (Android shows an ugly spinner, iOS shows a picker wheel).
 *
 * Web: renders a standard <select>.
 * Native: renders a styled trigger button + a bottom-sheet picker modal.
 *
 * Usage:
 *   <MobileSelect
 *     value={value}
 *     onChange={(v) => setValue(v)}
 *     options={[
 *       { value: '',  label: 'Select…' },
 *       { value: 'a', label: 'Option A' },
 *     ]}
 *     className="select"   // same className you'd put on <select>
 *     aria-label="My field"
 *     sheetTitle="Choose an option"  // optional header text in the bottom sheet
 *   />
 */

import { Capacitor } from '@capacitor/core';
import { ChevronDown, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { notifyModalOpen, notifyModalClose } from '../../utils/modalState';

const IS_NATIVE = Capacitor.isNativePlatform();

export interface MobileSelectOption {
  value: string;
  label: string;
}

interface MobileSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: MobileSelectOption[];
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /** Heading shown in the bottom-sheet picker on native. Falls back to aria-label. */
  sheetTitle?: string;
}

export const MobileSelect: React.FC<MobileSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
  style,
  id,
  'aria-label': ariaLabel,
  disabled,
  sheetTitle,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      notifyModalOpen();
      return () => notifyModalClose();
    }
  }, [open]);

  if (!IS_NATIVE) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        style={style}
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const sheet = open
    ? createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setOpen(false)}
          />
          {/* Bottom sheet — padded to clear the Android nav bar / iOS home indicator */}
          <div className="relative w-full bg-white rounded-t-2xl shadow-xl flex flex-col max-h-[70vh] overflow-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <span className="text-base font-semibold text-gray-900">
                {sheetTitle ?? ariaLabel ?? 'Select'}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 text-gray-500 rounded-full active:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`w-full px-4 py-3.5 text-left text-sm border-b border-gray-100 last:border-0 ${
                    opt.value === value
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-gray-900 active:bg-gray-50'
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={`${className} inline-flex items-center justify-between gap-1`}
        style={style}
      >
        <span className="flex-1 text-left truncate">{selectedLabel}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-400" aria-hidden />
      </button>
      {sheet}
    </>
  );
};
